import { StackScreenProps } from "@react-navigation/stack";
import { GiftCardPaymentRootParamList } from "../../../../types";
import React from "react";
import {
  Button,
  Page,
  TextField,
  useFlashMessages,
  View,
} from "@towersystems/roam-common-native";
import { GiftCard } from "@towersystems/roam-common/lib/generated-types";
import { QUERY_GIFT_CARD_BY_CODE } from "../../../../utilities/gift-card/constants";
import { useLazyQuery } from "@apollo/client";
import { useFocusEffect } from "@react-navigation/native";
import { useTranslation } from "react-i18next";

export interface SearchGiftCardScreenProps
  extends StackScreenProps<
    GiftCardPaymentRootParamList,
    "SearchGiftCardScreen"
  > {}

export const SearchGiftCardScreen = ({
  navigation,
}: SearchGiftCardScreenProps) => {
  const { t } = useTranslation("gift_card");
  const flashMessagesContext = useFlashMessages();

  const [giftCardNumberInput, setGiftCardNumberInput] =
    React.useState<string>("GV2be05c5407");

  const [giftCard, setGiftCard] = React.useState<GiftCard | undefined>(
    undefined
  );

  const [q, { loading }] = useLazyQuery<{
    giftCardByCode: GiftCard;
  }>(QUERY_GIFT_CARD_BY_CODE);

  useFocusEffect(
    React.useCallback(() => {
      setGiftCard(undefined);
    }, [])
  );

  React.useEffect(() => {
    if (giftCard) {
      navigation.navigate("GiftCardDetailsScreen", {
        giftCardNumber: giftCard.code,
      });
    }
  }, [giftCard]);

  React.useEffect(() => {
    if (giftCardNumberInput) {
      setGiftCard(undefined);
    }
  }, [giftCardNumberInput]);

  const handleOnSearch = React.useCallback((giftCardNumber: string) => {
    q({
      variables: {
        code: giftCardNumber,
      },
    })
      .then((res) => {
        if (res.data?.giftCardByCode) {
          setGiftCard(res.data.giftCardByCode);
        } else {
          throw new Error("Could not find gift card");
        }
      })
      .catch((e) => {
        flashMessagesContext.showMessage({
          message: e?.message ?? "Could not find gift card",
          severity: "error",
        });
      });
  }, []);

  const scrollable = true;

  const giftCardNumberInputMarkup = (
    <TextField
      placeholder={t("form.gift_card_number.placeholder")}
      value={giftCardNumberInput}
      onChangeText={(v) => setGiftCardNumberInput(v)}
    />
  );

  const footer = (
    <View>
      <Button
        onPress={() => {
          handleOnSearch(giftCardNumberInput);
        }}
        label={t("button.search_gift_card")}
        isAction
        disabled={!giftCardNumberInput || loading || !!giftCard}
        loading={loading}
      />
    </View>
  );

  return (
    <Page
      scrollable={scrollable}
      title={t("title.scan_or_enter_gift_card")}
      secondaryTitle={t("title.scan_or_enter_gift_card_secondary")}
      footer={footer}
    >
      {giftCardNumberInputMarkup}
    </Page>
  );
};
